import debounce from 'lodash.debounce';
import get from 'lodash.get';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Col, FormControl, Modal, Row } from 'react-bootstrap';
import { BsBriefcase, BsSearch } from 'react-icons/bs';
import { MdCancel } from 'react-icons/md';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/businessOnboarding';
import CustomToast from 'src/components/CustomToast';
import Logo from 'src/components/Logo';
import NoDataComponent from 'src/components/NoDataComponent';
import PaginationLayout from 'src/components/PaginationLayout';
import {
  ADDING_WAITLIST_ERROR,
  fetchingError,
  JOINING_COMMUNITY_ERROR,
  SOMETHING_WENT_WRONG,
} from 'src/containers/Core/Messages';
import { defaultPaginationObject, updatePaginationData } from 'src/utils/common';
import { BUSINESS_ONBOARDING_STEP, OnboardingStepProcess } from '.';
import { AppContext } from '../Core/AppContext';
import { AppState } from '../Core/AppReducer';
import BottomComponent from './Components/BottomComponent';
import BusinessItem from './Components/BusinessItem';
import JoinWaitlistModal from './Components/JoinWaitlistModal';

type JoinCommunityProps = {
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP, state: OnboardingStepProcess) => void;
  onExit: () => void;
} & RouteComponentProps;

const JoinCommunity: React.FunctionComponent<JoinCommunityProps> = ({
  setOnboardingStep,
  history,
  location,
  onExit,
}) => {
  const searchParams = qs.parse(location.search);
  const { query } = searchParams;
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const [loading, setLoading] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [search, setSearch] = useState(query ? query.toString() : '');
  const [showJoinConfirmation, setShowJoinConfirmation] = useState<EntityFields>(null);
  const [showJoinWailist, setShowJoinWailist] = useState(false);
  const [communityRelations, setCommunityRelations] = useState<PaginatedResponse<EntityFields>>(
    defaultPaginationObject()
  );
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  if (!businessUuid) {
    return null;
  }

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async (params?: PaginationParams & { search: string }) => {
    try {
      setLoading(true);
      const payload = await apiUtil.getAllCommunities({ ...params, businessUuid });
      setCommunityRelations(updatePaginationData(payload, communityRelations));
    } catch (error) {
      toast(<CustomToast title={SOMETHING_WENT_WRONG} description={fetchingError('Communities')} />);
    } finally {
      setLoading(false);
    }
  };

  const searchBizz = useCallback(
    debounce(
      (val: string) => {
        let searchString = qs.stringify({
          ...searchParams,
          ...(val ? { query: val } : { query: '' }),
        });

        if (!val) {
          searchString = searchString.replace('query=', '');
        }

        history.replace({
          search: searchString,
        });
        fetchCommunities({
          search: val || '',
        });
      },
      500,
      { leading: false, trailing: true }
    ),
    []
  );

  const joinCommunitySubmit = async () => {
    try {
      await apiUtil.joinCommunity(showJoinConfirmation.uuid, businessUuid);
      setShowJoinConfirmation(null);
      setOnboardingStep(
        get(showJoinConfirmation, 'settings.approvalRequired')
          ? BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMMUNITY
          : BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMPLETE,
        get(showJoinConfirmation, 'settings.approvalRequired') ? OnboardingStepProcess.PN : OnboardingStepProcess.CL
      );
    } catch {
      toast(<CustomToast title="Join Community" description={JOINING_COMMUNITY_ERROR} />);
    }
  };

  const joinWaitlistSubmit = async (comment: string) => {
    try {
      setWaitlistSubmitting(true);
      await apiUtil.addToWaitlist(businessUuid, { communityName: comment });
      setShowJoinWailist(false);
      setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_WAITLIST, OnboardingStepProcess.PN);
    } catch {
      toast(<CustomToast title="Join Waitlist" description={ADDING_WAITLIST_ERROR} />);
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const renderSearch = () => (
    <div className="mr-3 pt-3 pb-2 d-flex align-items-center search businesses-search">
      <BsSearch className="text-muted icon-search" />
      <FormControl
        value={search}
        type="text"
        placeholder="Search"
        className="rounded-pill"
        maxLength={100}
        size="sm"
        onChange={(e: React.ChangeEvent<{ value: string }>) => {
          setSearch(e.target.value);
          searchBizz(e.target.value);
        }}
      />
      <MdCancel
        size="1.2rem"
        className={`${search ? '' : 'd-none'} hover-danger icon-cancel`}
        onClick={() => {
          setSearch('');
          searchBizz('');
        }}
      />
    </div>
  );

  const renderList = () => {
    return (
      <PaginationLayout
        showMore={!!communityRelations.next || communityRelations.count > communityRelations.results.length}
        loadMore={() => {
          fetchCommunities({
            search: '',
            offset: communityRelations.results.length,
          });
        }}
        loading={loading}
        style={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}
      >
        {communityRelations.results.length || loading ? null : (
          <NoDataComponent className="h-100 pt-3" Icon={BsBriefcase} title="No communities found." />
        )}
        <div>
          {communityRelations.results.map((community: EntityFields, index: number) => {
            return (
              <div className="position-relative" key={index}>
                <BusinessItem
                  entity={community}
                  className="border-bottom"
                  renderRightElements={(b: EntityFields) => (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="px-3 mr-3"
                      onClick={(e: React.SyntheticEvent) => {
                        setShowJoinConfirmation(b);
                        e.stopPropagation();
                      }}
                    >
                      Join
                    </Button>
                  )}
                  showBadge={community.recommended}
                />
              </div>
            );
          })}
        </div>
      </PaginationLayout>
    );
  };

  const renderJoinCommunityModal = () => (
    <Modal show={!!showJoinConfirmation} onHide={() => setShowJoinConfirmation(null)}>
      <Modal.Header className="border-0 pb-0 font-size-16 font-weight-bold" closeButton />
      <Modal.Body className="pt-0">
        <div className="d-flex flex-column align-items-center">
          <Logo
            imageSrc={get(showJoinConfirmation, 'logo')}
            text={get(showJoinConfirmation, 'name')}
            height={40}
            width={40}
            variant="rounded-circle"
          />
          <strong className="my-3">{get(showJoinConfirmation, 'name')}</strong>
          <div className="my-3">
            <Button
              size="sm"
              variant="outline-secondary"
              className="mx-2 px-3"
              onClick={() => setShowJoinConfirmation(null)}
            >
              No, Cancel
            </Button>
            <Button size="sm" className="px-3" onClick={joinCommunitySubmit}>
              Yes, Join
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );

  return (
    <div>
      {renderJoinCommunityModal()}
      <JoinWaitlistModal
        onSubmit={joinWaitlistSubmit}
        onHide={() => setShowJoinWailist(false)}
        show={showJoinWailist}
        submitting={waitlistSubmitting}
      />
      <div className="overflow-auto h-100 p-3">
        <div className="d-flex flex-column">
          <strong>Select the community your business belongs to</strong>
        </div>
        <Row>
          <Col sm={12} md={6}>
            {renderSearch()}
            {renderList()}
          </Col>
        </Row>
      </div>
      <BottomComponent className="justify-content-between">
        <span>
          <small className="text-muted">Didn’t find a community for your business?</small>
          <Button className="ml-2" onClick={() => setShowJoinWailist(true)}>
            <small>Join Waitlist</small>
          </Button>
        </span>
        <Button className="px-2" size="sm" variant="outline-primary" onClick={onExit}>
          Quit
        </Button>
      </BottomComponent>
    </div>
  );
};

export default withRouter(JoinCommunity);
